import { useState } from "react";
import { type FacebookGroup } from "@/lib/supabaseClient";

type Props = {
  group: FacebookGroup;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onFeature: (id: string, featured: boolean) => void;
  onDelete: (id: string) => void;
  onReopen: (id: string) => void;
  loading: boolean;
};

export default function AdminGroupCard({
  group,
  onApprove,
  onReject,
  onFeature,
  onDelete,
  onReopen,
  loading,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const topics = (group.topics || "").split(",").map((t) => t.trim()).filter(Boolean);

  const statusColors: Record<string, string> = {
    pending: "bg-amber-100 text-amber-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-600",
  };

  const statusIcons: Record<string, string> = {
    pending: "ri-time-line",
    approved: "ri-checkbox-circle-line",
    rejected: "ri-close-circle-line",
  };

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden transition-all ${group.status === "pending" ? "border-amber-200" : "border-[#ede5d8]"}`}>
      {/* Main Row */}
      <div className="flex flex-col sm:flex-row">
        {/* Banner / Image */}
        <div className="w-full sm:w-36 h-32 shrink-0 bg-[#f5ede0] flex items-center justify-center overflow-hidden">
          {group.image_url ? (
            <img
              src={group.image_url}
              alt={group.group_name}
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#ede5d8]">
              <i className="ri-facebook-circle-fill text-3xl text-[#c9a87c]"></i>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
            <div>
              <h3 className="font-bold text-[#2c1e0f] text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
                {group.group_name}
              </h3>
              <p className="text-xs text-[#7a6555]">Admin: {group.admin_name} · {group.email}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize flex items-center gap-1 ${statusColors[group.status] || statusColors.pending}`}>
                <i className={statusIcons[group.status] || "ri-time-line"}></i>
                {group.status}
              </span>
              {group.featured && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#f5ede0] text-[#8b5e3c] flex items-center gap-1">
                  <i className="ri-star-fill"></i> Featured
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-2 text-xs text-[#9a8a78]">
            {group.group_type && (
              <span className="text-xs text-[#8b5e3c] font-medium bg-[#f5ede0] px-2 py-0.5 rounded-full">
                {group.group_type}
              </span>
            )}
            {group.member_count && (
              <span className="flex items-center gap-1">
                <i className="ri-group-line"></i> {group.member_count} members
              </span>
            )}
            {group.location && (
              <span className="flex items-center gap-1">
                <i className="ri-map-pin-line"></i>{group.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {new Date(group.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
          </div>

          {!expanded && group.description && (
            <p className="text-xs text-[#6b5a4a] leading-relaxed mb-2 line-clamp-2">{group.description}</p>
          )}

          {topics.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {topics.map((t) => (
                <span key={t} className="px-2 py-0.5 rounded-full bg-[#f5ede0] text-[#8b5e3c] text-xs">
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 items-center">
            {group.status !== "approved" && (
              <button
                onClick={() => onApprove(group.id)}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-check-line mr-1"></i>Approve
              </button>
            )}
            {group.status !== "rejected" && (
              <button
                onClick={() => onReject(group.id)}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-close-line mr-1"></i>Reject
              </button>
            )}
            {group.status !== "pending" && (
              <button
                onClick={() => onReopen(group.id)}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg bg-[#f5ede0] text-[#8b5e3c] text-xs font-semibold hover:bg-[#ede0cc] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-refresh-line mr-1"></i>Re-open
              </button>
            )}
            {group.status === "approved" && (
              <button
                onClick={() => onFeature(group.id, !group.featured)}
                disabled={loading}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                  group.featured
                    ? "bg-[#f5ede0] text-[#8b5e3c] hover:bg-[#ede0cc]"
                    : "bg-[#8b5e3c] text-white hover:bg-[#7a5234]"
                }`}
              >
                <i className={`mr-1 ${group.featured ? "ri-star-line" : "ri-star-fill"}`}></i>
                {group.featured ? "Unfeature" : "Feature"}
              </button>
            )}

            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setExpanded(!expanded)}
                className="px-3 py-1.5 rounded-lg border border-[#ede5d8] text-[#7a6555] text-xs font-medium hover:bg-[#faf8f4] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className={`${expanded ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                {expanded ? "Less" : "Full Details"}
              </button>

              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={loading}
                  className="px-3 py-1.5 rounded-lg border border-red-200 text-red-400 text-xs font-medium hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-red-500 font-medium">Delete?</span>
                  <button
                    onClick={() => { onDelete(group.id); setConfirmDelete(false); }}
                    disabled={loading}
                    className="px-2 py-1 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="px-2 py-1 rounded-lg bg-[#f5ede0] text-[#8b5e3c] text-xs font-semibold hover:bg-[#ede0cc] cursor-pointer whitespace-nowrap"
                  >
                    No
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Detail Panel */}
      {expanded && (
        <div className="border-t border-[#ede5d8] bg-[#faf8f4] px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Description */}
            {group.description && (
              <div className="sm:col-span-2">
                <p className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5">Description</p>
                <p className="text-sm text-[#4a3728] leading-relaxed bg-white rounded-xl border border-[#ede5d8] px-4 py-3">
                  {group.description}
                </p>
              </div>
            )}

            {/* Facebook Link */}
            <div>
              <p className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-2">Facebook Group</p>
              <a
                href={group.facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#8b5e3c] hover:underline flex items-center gap-1.5 truncate"
              >
                <i className="ri-facebook-circle-line shrink-0"></i>
                <span className="truncate">{group.facebook_url}</span>
              </a>
            </div>

            {/* Details */}
            <div>
              <p className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-2">Details</p>
              <div className="flex flex-col gap-1.5 text-sm text-[#7a6555]">
                <span><span className="font-medium text-[#3a2e1e]">Admin Email:</span> {group.email}</span>
                {group.member_count && <span><span className="font-medium text-[#3a2e1e]">Members:</span> {group.member_count}</span>}
                {group.group_type && <span><span className="font-medium text-[#3a2e1e]">Type:</span> {group.group_type}</span>}
                {group.location && <span><span className="font-medium text-[#3a2e1e]">Location:</span> {group.location}</span>}
                <span><span className="font-medium text-[#3a2e1e]">Submitted:</span> {new Date(group.created_at).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
